/*
 * File: Orders.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Display list of orders. List is paginated, so more orders
 * will be loaded as user scrolls down
 */
import React from "react";
import UINotFound from "components/ui/UINotFound";
import OrderCard from "./OrderCard";
import {
  InfiniteLoader,
  List,
  AutoSizer,
  CellMeasurerCache,
  CellMeasurer
} from "react-virtualized";
import PropTypes from "prop-types";
import { PropTypes as MobxPropTypes } from "mobx-react";
import FabSpacer from "../../../components/ui/FabSpacer";
import { withStyles } from "@material-ui/core/styles";

const styles = theme => ({
  cardList: {
    marginTop: theme.spacing(-2)
  }
});

const Orders = ({
  orders,
  hasNextPage,
  isNextPageLoading,
  loadNextPage,
  classes
}) => {
  if (!orders.length) {
    return <UINotFound message="No orders were found" />;
  }

  //Used by CellMeasurer as boundaries to get the correct rowHeight
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 165,
    minHeight: 100
  });

  const rowCount = hasNextPage ? orders.length + 1 : orders.length;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const loadMoreRows = isNextPageLoading ? () => {} : loadNextPage;

  const isRowLoaded = ({ index }) => !hasNextPage || index < orders.length;

  const renderRow = params => {
    const { index, key, style, parent } = params;
    const content = isRowLoaded({ index: index }) ? (
      <OrderCard order={orders[index]} />
    ) : (
      "Loading..."
    );
    return (
      //Measures the height of each row so the rowHeight can dynamically change
      <CellMeasurer cache={cache} key={key} parent={parent} rowIndex={index}>
        <div style={style}>{content}</div>
      </CellMeasurer>
    );
  };

  return (
    <React.Fragment>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={rowCount}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                className={classes.cardList}
                ref={registerChild}
                width={width}
                height={window.innerHeight}
                onRowsRendered={onRowsRendered}
                rowHeight={cache.rowHeight}
                rowCount={rowCount}
                rowRenderer={renderRow}
                overscanRowCount={5}
              />
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
      <FabSpacer />
    </React.Fragment>
  );
};

Orders.propTypes = {
  orders: MobxPropTypes.observableArray.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  isNextPageLoading: PropTypes.bool.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(Orders);
